import { resolveBusinessAvatarUrl } from "@/lib/businessImages";

export const MAX_ACTIVE_STORIES_PER_BUSINESS = 5;
export const STORY_CAPTION_MAX_LENGTH = 220;

export type StoryCategory =
  | "new_arrival"
  | "available_today"
  | "behind_the_scenes"
  | "event"
  | "other";

export type StorySummary = {
  id: string;
  businessId: string;
  createdAt: string;
  expiresAt: string;
  mediaType: "image" | "video";
  mediaUrl: string;
  mediaThumbUrl: string | null;
  durationMs?: number | null;
  caption: string | null;
  category: StoryCategory | null;
  ctaType: "business" | "listing" | null;
  ctaListingId: string | null;
  ctaListingPublicId: string | null;
};

export type StoryBusinessGroup = {
  businessId: string;
  ownerUserId: string;
  publicId: string | null;
  businessName: string;
  avatarUrl: string | null;
  stories: StorySummary[];
};

export type StoryBusinessDiscoveryItem = {
  businessId: string;
  publicId: string | null;
  businessName: string;
  avatarUrl: string | null;
  newestStoryAt: string;
  storyCount: number;
};

export type StoryBusinessTrayItem = StoryBusinessGroup | StoryBusinessDiscoveryItem;
export type SeenStoryBusinessMap = Record<string, string>;

export function getStoryDiscoveryLocationKey(location: any) {
  const city = String(location?.city || "").trim().toLowerCase();
  const state = String(location?.region || location?.state || "").trim().toUpperCase();
  return `${city}:${state}`;
}

export function hasStoryDiscoveryLocationChanged(current: any, initial: any) {
  return getStoryDiscoveryLocationKey(current) !== getStoryDiscoveryLocationKey(initial);
}

export function isStoryBusinessGroup(item: StoryBusinessTrayItem): item is StoryBusinessGroup {
  return Array.isArray((item as StoryBusinessGroup)?.stories);
}

export function getStoryBusinessNewestAt(item: StoryBusinessTrayItem) {
  if (!isStoryBusinessGroup(item)) return item.newestStoryAt;
  return item.stories.reduce((latest, story) => (
    story.createdAt > latest ? story.createdAt : latest
  ), "");
}

export function isStoryBusinessSeen(
  item: StoryBusinessTrayItem,
  seenBusinesses: SeenStoryBusinessMap
) {
  const seenAt = new Date(seenBusinesses[item.businessId] || 0).getTime();
  const newestAt = new Date(getStoryBusinessNewestAt(item) || 0).getTime();
  return Number.isFinite(seenAt) && Number.isFinite(newestAt) && seenAt >= newestAt;
}

export function orderStoryBusinessesBySeen<T extends StoryBusinessTrayItem>(
  items: T[],
  seenBusinesses: SeenStoryBusinessMap
) {
  const unseen: T[] = [];
  const seen: T[] = [];
  for (const item of items) {
    (isStoryBusinessSeen(item, seenBusinesses) ? seen : unseen).push(item);
  }
  return [...unseen, ...seen];
}

export function appendUniqueStoryBusinesses<T extends StoryBusinessTrayItem>(
  current: T[],
  incoming: T[]
) {
  const byBusiness = new Map(current.map((item) => [item.businessId, item]));
  for (const item of incoming) {
    if (!byBusiness.has(item.businessId)) byBusiness.set(item.businessId, item);
  }
  return Array.from(byBusiness.values());
}

const STORY_SELECT = [
  "id",
  "business_id",
  "media_type",
  "media_url",
  "media_thumb_url",
  "duration_ms",
  "caption",
  "category",
  "cta_type",
  "cta_listing_id",
  "created_at",
  "expires_at",
  "deleted_at",
  "moderated_at",
].join(",");

export const STORY_MEDIA_ASSET_SELECT = [
  "id",
  "business_id",
  "purpose",
  "status",
  "processing_state",
  "mime_type",
  "source_path",
  "original_path",
  "detail_path",
  "public_url",
  "committed_at",
].join(",");

function clean(value: unknown) {
  const text = String(value || "").trim();
  return text || null;
}

export function isStoryMediaAssetReady({
  asset,
  businessId,
  mediaType,
}: {
  asset: any;
  businessId: string;
  mediaType: "image" | "video";
}) {
  const canonicalPath = clean(asset?.detail_path)
    || clean(asset?.original_path)
    || clean(asset?.source_path)
    || clean(asset?.public_url);
  if (
    !asset
    || String(asset.business_id || "") !== String(businessId || "")
    || asset.purpose !== "business_story"
    || asset.status !== "active"
    || asset.processing_state !== "complete"
    || !asset.committed_at
    || !canonicalPath
  ) return false;

  const mimeType = String(asset.mime_type || "").toLowerCase();
  return mediaType === "video"
    ? mimeType === "video/mp4" && Boolean(clean(asset.source_path) || clean(asset.detail_path))
    : ["image/jpeg", "image/png", "image/webp"].includes(mimeType);
}

export function isPublicStorySummaryUsable(story: StorySummary, now = Date.now()) {
  const expiresAt = new Date(story?.expiresAt || 0).getTime();
  const mediaUrl = clean(story?.mediaUrl);
  if (!Number.isFinite(expiresAt) || expiresAt <= now || !mediaUrl) return false;
  if (story.mediaType === "image") return true;
  return story.mediaType === "video" && /\.mp4(?:$|[?#])/i.test(mediaUrl);
}

export function filterPublicStoryGroups(groups: StoryBusinessGroup[], now = Date.now()) {
  return (Array.isArray(groups) ? groups : []).flatMap((group) => {
    const stories = (Array.isArray(group?.stories) ? group.stories : [])
      .filter((story) => isPublicStorySummaryUsable(story, now));
    return stories.length ? [{ ...group, stories }] : [];
  });
}

function mapStory(row: any, listingRefs: Map<string, string>): StorySummary {
  const listingId = clean(row?.cta_listing_id);
  return {
    id: String(row.id),
    businessId: String(row.business_id),
    createdAt: String(row.created_at),
    expiresAt: String(row.expires_at),
    mediaType: row.media_type === "video" ? "video" : "image",
    mediaUrl: String(row.media_url),
    mediaThumbUrl: clean(row.media_thumb_url),
    durationMs:
      row.duration_ms != null && Number.isFinite(Number(row.duration_ms))
        ? Number(row.duration_ms)
        : null,
    caption: clean(row.caption),
    category: (clean(row.category) as StoryCategory | null) || null,
    ctaType: row.cta_type === "listing" || row.cta_type === "business" ? row.cta_type : null,
    ctaListingId: listingId,
    ctaListingPublicId: listingId ? listingRefs.get(listingId) || null : null,
  };
}

async function loadStoryRows(client: any, businessIds: string[]) {
  if (!businessIds.length) return [];
  const { data, error } = await client
    .from("public_business_stories_v")
    .select(STORY_SELECT)
    .in("business_id", businessIds)
    .is("deleted_at", null)
    .is("moderated_at", null)
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: true })
    .limit(Math.min(businessIds.length * MAX_ACTIVE_STORIES_PER_BUSINESS, 100));
  if (error) {
    if (["42P01", "PGRST204"].includes(String(error.code || ""))) return [];
    throw error;
  }
  const now = Date.now();
  return (Array.isArray(data) ? data : []).filter((row) => {
    const expiresAt = new Date(row?.expires_at || 0).getTime();
    const mediaUrl = clean(row?.media_url);
    return Number.isFinite(expiresAt)
      && expiresAt > now
      && !row?.deleted_at
      && !row?.moderated_at
      && Boolean(mediaUrl)
      && (row?.media_type !== "video" || /\.mp4(?:$|[?#])/i.test(mediaUrl || ""));
  });
}

async function loadListingRefs(client: any, rows: any[]) {
  const ids = Array.from(new Set(rows.map((row) => clean(row?.cta_listing_id)).filter(Boolean))) as string[];
  if (!ids.length) return new Map<string, string>();
  const { data, error } = await client
    .from("public_listings_v")
    .select("id,public_id")
    .in("id", ids);
  if (error) return new Map<string, string>();
  return new Map<string, string>(
    (data || []).map((row: any): [string, string] => [String(row.id), String(row.public_id || row.id)])
  );
}

export async function getPublicStoryGroupsForBusinesses({
  client,
  businesses,
}: {
  client: any;
  businesses: any[];
}): Promise<StoryBusinessGroup[]> {
  const eligibleBusinesses = (Array.isArray(businesses) ? businesses : []).filter((row) => row?.id);
  const rows = await loadStoryRows(
    client,
    eligibleBusinesses.map((row) => String(row.id))
  );
  if (!rows.length) return [];
  const listingRefs = await loadListingRefs(client, rows);
  const storiesByBusiness = new Map<string, StorySummary[]>();
  for (const row of rows) {
    const key = String(row.business_id);
    const group = storiesByBusiness.get(key) || [];
    group.push(mapStory(row, listingRefs));
    storiesByBusiness.set(key, group);
  }

  return eligibleBusinesses.flatMap((business) => {
    const stories = storiesByBusiness.get(String(business.id)) || [];
    if (!stories.length) return [];
    return [{
      businessId: String(business.id),
      ownerUserId: String(business.owner_user_id),
      publicId: clean(business.public_id),
      businessName: clean(business.business_name) || "Local business",
      avatarUrl: resolveBusinessAvatarUrl(business),
      stories,
    }];
  });
}
